import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Save, Upload, Trash2, Image as ImageIcon, Palette, FileText, 
  Settings2, Eye, EyeOff, GripVertical, RotateCcw, Loader2,
  CheckCircle, AlertCircle, Columns, ChevronRight, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_TERMS_OPTIONS = [
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'NET_7', label: 'Net 7 Days' },
  { value: 'NET_15', label: 'Net 15 Days' },
  { value: 'NET_30', label: 'Net 30 Days' },
  { value: 'NET_45', label: 'Net 45 Days' },
  { value: 'NET_60', label: 'Net 60 Days' },
  { value: 'NET_90', label: 'Net 90 Days' },
];

const COLOR_PRESETS = [
  { name: 'Gold', value: '#D4A017' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Green', value: '#16A34A' },
  { name: 'Purple', value: '#7C3AED' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Slate', value: '#475569' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Orange', value: '#EA580C' },
];

const TEMPLATE_OPTIONS = [
  { 
    id: 'classic', 
    name: 'Classic', 
    description: 'Professional two-column layout with logo on left',
    icon: '📄'
  },
  { 
    id: 'modern', 
    name: 'Modern', 
    description: 'Clean centered layout with more whitespace',
    icon: '✨'
  },
];

const DEFAULT_COLUMNS = [
  { id: 'item', title: 'ITEM / DESCRIPTION', visible: true, width: 4.0 },
  { id: 'qty', title: 'QTY', visible: true, width: 0.75 },
  { id: 'rate', title: 'UNIT PRICE', visible: true, width: 1.1 },
  { id: 'amount', title: 'AMOUNT', visible: true, width: 1.15 },
];

const TABS = [
  { id: 'style', label: 'Style', icon: Palette },
  { id: 'columns', label: 'Columns', icon: Columns },
  { id: 'invoice', label: 'Invoice Defaults', icon: FileText },
  { id: 'quote', label: 'Quote Defaults', icon: FileText },
  { id: 'receipt', label: 'Payment Receipt', icon: FileText },
];

const AdminInvoiceSettings = () => {
  const [activeTab, setActiveTab] = useState('style');
  const [settings, setSettings] = useState({
    company_name: 'Corporate Awards & Engraving',
    company_address: '1489 E. Warner Ave, Santa Ana, CA 92705',
    company_phone: '(949) 261-7130',
    company_email: 'ca@engraveca.com',
    company_website: 'www.engraveca.com',
    logo_url: null,
    logo_position: 'left',
    accent_color: '#D4A017',
    template: 'classic',
    default_payment_terms: 'NET_30',
    default_notes: 'Thank you for your business!',
    default_terms: 'Payment due within terms specified. Late payments may be subject to a 1.5% monthly interest charge.',
    quote_validity_days: 30,
    quote_default_notes: 'This quote is valid for 30 days from the date issued.',
    payment_receipt_note: '',
    include_logo: true,
    columns: DEFAULT_COLUMNS,
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewDebounceRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings/invoice`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ 
          ...prev, 
          ...data,
          columns: data.columns || DEFAULT_COLUMNS 
        }));
      }
    } catch (err) {
      console.error('Failed to fetch invoice settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Generate preview PDF when settings change
  const generatePreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/site-settings/invoice/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        const blob = await res.blob();
        // Convert blob to data URL for better browser compatibility
        const reader = new FileReader();
        reader.onloadend = () => {
          // Revoke old URL if it was a blob URL
          if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
          }
          setPreviewUrl(reader.result);
        };
        reader.onerror = () => {
          console.error('Failed to convert blob to data URL');
        };
        reader.readAsDataURL(blob);
      } else {
        console.error('Preview generation failed:', res.status, res.statusText);
      }
    } catch (err) {
      console.error('Failed to generate preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  }, [settings]); // Remove previewUrl from deps to avoid stale closure issues

  // Debounced preview generation
  useEffect(() => {
    if (loading) return;
    
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
    }
    
    previewDebounceRef.current = setTimeout(() => {
      generatePreview();
    }, 500);
    
    return () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }
    };
  }, [settings, loading]);

  // Cleanup preview URL on unmount (only needed for blob URLs)
  useEffect(() => {
    const currentUrl = previewUrl;
    return () => {
      if (currentUrl && currentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [previewUrl]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleColumnChange = (index, field, value) => {
    setSettings(prev => {
      const newColumns = [...prev.columns];
      newColumns[index] = { ...newColumns[index], [field]: value };
      return { ...prev, columns: newColumns };
    });
    setHasChanges(true);
  };

  const resetColumns = () => {
    setSettings(prev => ({ ...prev, columns: DEFAULT_COLUMNS }));
    setHasChanges(true);
    toast.info('Columns reset to defaults');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/site-settings/invoice`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to save');
      }
      
      const data = await res.json();
      setSettings(prev => ({ ...prev, ...data }));
      setHasChanges(false);
      toast.success('Settings saved successfully');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, SVG, or WebP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/site-settings/invoice/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await res.json();
      setSettings(prev => ({ ...prev, logo_url: data.logo_url }));
      setHasChanges(true);
      toast.success('Logo uploaded successfully');
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the logo?')) return;

    try {
      const res = await fetch(`${API_URL}/api/site-settings/invoice/logo`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Failed to delete logo');
      }

      setSettings(prev => ({ ...prev, logo_url: null }));
      setHasChanges(true);
      toast.success('Logo deleted');
    } catch (err) {
      console.error('Logo delete failed:', err);
      toast.error('Failed to delete logo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="invoice-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Invoice & Quote Settings</h2>
          <p className="text-sm text-gray-500">Customize your PDF templates with live preview</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Unsaved changes
            </span>
          )}
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Settings */}
        <div className="w-[55%] border-r bg-gray-50 overflow-y-auto">
          {/* Tab Navigation */}
          <div className="flex border-b bg-white sticky top-0 z-10">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Style Tab */}
            {activeTab === 'style' && (
              <div className="space-y-6">
                {/* Logo Section */}
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Company Logo
                  </h3>
                  
                  <div className="flex items-start gap-4">
                    {settings.logo_url ? (
                      <div className="flex-shrink-0">
                        <div className="w-32 h-20 border rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden">
                          <img 
                            src={`${API_URL}${settings.logo_url}`} 
                            alt="Logo" 
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <div className="flex gap-2 mt-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => document.getElementById('logo-upload').click()}
                            disabled={uploading}
                          >
                            Replace
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={handleLogoDelete}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="w-32 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                        onClick={() => document.getElementById('logo-upload').click()}
                      >
                        {uploading ? (
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-gray-400" />
                            <span className="text-xs text-gray-500 mt-1">Upload</span>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={settings.include_logo}
                          onCheckedChange={(checked) => handleChange('include_logo', checked)}
                          data-testid="include-logo-switch"
                        />
                        <Label className="text-sm">Show logo on invoices & quotes</Label>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, SVG or WebP. Max 5MB. Recommended: 400x100px
                      </p>
                    </div>
                  </div>
                  
                  <input
                    id="logo-upload"
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.webp"
                    className="hidden"
                    onChange={handleLogoUpload}
                    data-testid="logo-upload-input"
                  />
                </div>

                {/* Company Info Section */}
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Company Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-xs text-gray-600">Company Name</Label>
                      <Input
                        value={settings.company_name}
                        onChange={(e) => handleChange('company_name', e.target.value)}
                        className="mt-1"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-gray-600">Address</Label>
                      <Input
                        value={settings.company_address}
                        onChange={(e) => handleChange('company_address', e.target.value)}
                        className="mt-1"
                        data-testid="company-address-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Phone</Label>
                      <Input
                        value={settings.company_phone}
                        onChange={(e) => handleChange('company_phone', e.target.value)}
                        className="mt-1"
                        data-testid="company-phone-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Email</Label>
                      <Input
                        value={settings.company_email}
                        onChange={(e) => handleChange('company_email', e.target.value)}
                        className="mt-1"
                        data-testid="company-email-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-gray-600">Website</Label>
                      <Input
                        value={settings.company_website}
                        onChange={(e) => handleChange('company_website', e.target.value)}
                        className="mt-1"
                        data-testid="company-website-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Template Selection Section */}
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Template Layout
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {TEMPLATE_OPTIONS.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleChange('template', template.id)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          settings.template === template.id
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`template-${template.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{template.icon}</span>
                          <span className="font-medium text-gray-900">{template.name}</span>
                          {settings.template === template.id && (
                            <Check className="w-4 h-4 text-blue-500 ml-auto" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{template.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Accent Color Section */}
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Accent Color
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg border shadow-sm flex-shrink-0"
                        style={{ backgroundColor: settings.accent_color }}
                      />
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.accent_color}
                          onChange={(e) => handleChange('accent_color', e.target.value)}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={settings.accent_color}
                          onChange={(e) => handleChange('accent_color', e.target.value)}
                          className="w-28 font-mono text-sm"
                          data-testid="accent-color-input"
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          className={`w-8 h-8 rounded-lg border-2 transition-all ${
                            settings.accent_color === preset.value 
                              ? 'border-gray-900 ring-2 ring-offset-1 ring-gray-300' 
                              : 'border-transparent hover:border-gray-300'
                          }`}
                          style={{ backgroundColor: preset.value }}
                          onClick={() => handleChange('accent_color', preset.value)}
                          title={preset.name}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Columns Tab */}
            {activeTab === 'columns' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Columns className="w-4 h-4" />
                      Column Configuration
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetColumns}
                      className="text-gray-500"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-4">
                    Customize column titles and visibility for your line items table.
                  </p>
                  
                  <div className="space-y-3">
                    {settings.columns.map((column, index) => (
                      <div 
                        key={column.id}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                      >
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                        
                        <div className="flex-1">
                          <Input
                            value={column.title}
                            onChange={(e) => handleColumnChange(index, 'title', e.target.value)}
                            className="h-9 text-sm"
                            placeholder="Column title"
                            data-testid={`column-title-${column.id}`}
                          />
                        </div>
                        
                        <button
                          onClick={() => handleColumnChange(index, 'visible', !column.visible)}
                          className={`p-2 rounded-lg transition-colors ${
                            column.visible 
                              ? 'bg-blue-100 text-blue-600' 
                              : 'bg-gray-200 text-gray-400'
                          }`}
                          title={column.visible ? 'Hide column' : 'Show column'}
                          data-testid={`column-visibility-${column.id}`}
                        >
                          {column.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-4">
                    Changes will be reflected in the preview. Save to apply to all future invoices and quotes.
                  </p>
                </div>
              </div>
            )}

            {/* Invoice Defaults Tab */}
            {activeTab === 'invoice' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Invoice Defaults</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-gray-600">Default Payment Terms</Label>
                      <Select
                        value={settings.default_payment_terms}
                        onValueChange={(value) => handleChange('default_payment_terms', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="payment-terms-select">
                          <SelectValue placeholder="Select terms" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_TERMS_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs text-gray-600">Default Notes</Label>
                      <Textarea
                        value={settings.default_notes}
                        onChange={(e) => handleChange('default_notes', e.target.value)}
                        className="mt-1"
                        rows={3}
                        placeholder="Thank you for your business!"
                        data-testid="default-notes-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This message appears at the bottom of invoices.
                      </p>
                    </div>

                    <div>
                      <Label className="text-xs text-gray-600">Terms & Conditions</Label>
                      <Textarea
                        value={settings.default_terms}
                        onChange={(e) => handleChange('default_terms', e.target.value)}
                        className="mt-1"
                        rows={4}
                        placeholder="Payment terms and conditions..."
                        data-testid="default-terms-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quote Defaults Tab */}
            {activeTab === 'quote' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Quote Defaults</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-gray-600">Quote Validity (Days)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={settings.quote_validity_days}
                        onChange={(e) => handleChange('quote_validity_days', parseInt(e.target.value) || 30)}
                        className="mt-1 w-32"
                        data-testid="quote-validity-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Number of days quotes are valid from issue date.
                      </p>
                    </div>

                    <div>
                      <Label className="text-xs text-gray-600">Default Quote Notes</Label>
                      <Textarea
                        value={settings.quote_default_notes}
                        onChange={(e) => handleChange('quote_default_notes', e.target.value)}
                        className="mt-1"
                        rows={3}
                        placeholder="This quote is valid for..."
                        data-testid="quote-notes-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Receipt Tab */}
            {activeTab === 'receipt' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg border p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Payment Receipt Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-gray-600">Receipt Note</Label>
                      <Textarea
                        value={settings.payment_receipt_note || ''}
                        onChange={(e) => handleChange('payment_receipt_note', e.target.value)}
                        className="mt-1"
                        rows={4}
                        placeholder="Add a custom note to appear on all payment receipts (e.g., return policy, contact info, etc.)"
                        data-testid="receipt-note-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This note will appear on all payment receipt PDFs, above the "Thank you" message.
                      </p>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-800 mb-2">Payment Receipt Format</h4>
                      <p className="text-xs text-green-700">
                        Payment receipts use the invoice template style with a green accent color. 
                        They include: company header, PAID status badge, bill to/ship to addresses, 
                        line items, totals, payment details, and your custom note.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Live Preview */}
        <div className="w-[45%] bg-gray-100 flex flex-col">
          <div className="px-4 py-3 border-b bg-white flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Live Preview
            </span>
            {previewLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>
          
          <div className="flex-1 p-4 overflow-auto">
            {previewUrl ? (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full">
                <object
                  data={previewUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  title="Invoice Preview"
                >
                  <iframe
                    src={previewUrl}
                    className="w-full h-full border-0"
                    title="Invoice Preview"
                  />
                </object>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm">Generating preview...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInvoiceSettings;
